/**
 * Carica un allegato email nel sistema come preventivo: 3 modi (crea nuovo,
 * popola un preventivo vuoto esistente, allega documento firmato a un
 * preventivo con eventuale generazione ordine di vendita). Picker searchable
 * per soggetto/preventivo/cartella (regola HARD, no liste hardcoded).
 *
 * Feedback operazioni: stesso pattern inline del Task 6 (SalvaDocumentaleDialog) -
 * nessun <Toaster/> montato nell'app, quindi niente sonner/toast qui: errore in
 * rosso, esito positivo in verde.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@floraltech/ui';
import { emailFetch, type EmailAllegato } from '../lib/api';

interface Soggetto {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
}

interface Preventivo {
  id: string;
  numero: string;
  tipo: 'acquisto' | 'vendita';
}

interface Cartella {
  id: string;
  parent_id: string | null;
  nome: string;
  percorso_completo: string;
}

interface CaricaPreventivoResponse {
  preventivo_id: string;
  tipo: string;
  numero: string;
  stato: string;
  righe_estratte: number;
  riferimento: string | null;
  documento_id: string | null;
  ordine_id: string | null;
  ordine_numero: string | null;
}

interface Props {
  messaggioId: string;
  allegato: EmailAllegato | null;
  onClose: () => void;
}

type Modo = 'crea' | 'popola' | 'allega';

const CHIUSURA_SUCCESSO_MS = 1500;

function nomeSoggetto(s: Soggetto): string {
  return s.ragione_sociale || [s.nome, s.cognome].filter(Boolean).join(' ') || '-';
}

export default function CaricaSistemaDialog({ messaggioId, allegato, onClose }: Props) {
  const navigate = useNavigate();

  const [modo, setModo] = useState<Modo>('crea');
  const [tipo, setTipo] = useState<'vendita' | 'acquisto'>('vendita');

  const [clienti, setClienti] = useState<Soggetto[]>([]);
  const [fornitori, setFornitori] = useState<Soggetto[]>([]);
  const [soggettoId, setSoggettoId] = useState<string | null>(null);
  const [filtroSoggetto, setFiltroSoggetto] = useState('');

  const [preventivi, setPreventivi] = useState<Preventivo[]>([]);
  const [preventivoId, setPreventivoId] = useState<string | null>(null);
  const [filtroPreventivo, setFiltroPreventivo] = useState('');

  const [cartelle, setCartelle] = useState<Cartella[]>([]);
  const [cartellaId, setCartellaId] = useState<string | null>(null);
  const [filtroCartella, setFiltroCartella] = useState('');

  const [generaOrdine, setGeneraOrdine] = useState(false);
  const [riferimento, setRiferimento] = useState('');

  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [nessunaRiga, setNessunaRiga] = useState(false);
  const [messaggio, setMessaggio] = useState<string | null>(null);

  // Identifica la "sessione" corrente (incrementata a ogni apertura/chiusura
  // del dialog) per scartare risposte tardive di fetch (GET iniziali e POST
  // di submit, incluse le ripetizioni con forza_vuoto) avviate su un
  // allegato precedente. Il timeout di chiusura+navigazione automatica viene
  // tracciato per poterlo cancellare se il dialog cambia allegato prima che
  // scatti.
  const generazioneRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset dello stato del form a ogni apertura e caricamento delle liste
  // necessarie (soggetti, preventivi, cartelle documentale).
  useEffect(() => {
    generazioneRef.current += 1;
    const generazione = generazioneRef.current;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (!allegato) return;
    setModo('crea');
    setTipo('vendita');
    setSoggettoId(null);
    setFiltroSoggetto('');
    setPreventivoId(null);
    setFiltroPreventivo('');
    setCartellaId(null);
    setFiltroCartella('');
    setGeneraOrdine(false);
    setRiferimento('');
    setInCorso(false);
    setErrore(null);
    setNessunaRiga(false);
    setMessaggio(null);

    const token = localStorage.getItem('token');
    const authGet = { headers: { Authorization: `Bearer ${token ?? ''}` } };
    const segnalaErroreCaricamento = (etichetta: string) => {
      if (generazioneRef.current === generazione) {
        setErrore((prev) => prev ?? `Errore nel caricamento: ${etichetta}`);
      }
    };

    fetch('/api/v1/soggetti?tipo=cliente&limit=500', authGet)
      .then((r) => {
        if (!r.ok) {
          segnalaErroreCaricamento('clienti');
          return [];
        }
        return r.json();
      })
      .then((data) => {
        if (generazioneRef.current === generazione) {
          setClienti(Array.isArray(data) ? data : data.items ?? []);
        }
      })
      .catch(() => {
        segnalaErroreCaricamento('clienti');
        if (generazioneRef.current === generazione) setClienti([]);
      });

    fetch('/api/v1/soggetti?tipo=fornitore&limit=200', authGet)
      .then((r) => {
        if (!r.ok) {
          segnalaErroreCaricamento('fornitori');
          return [];
        }
        return r.json();
      })
      .then((data) => {
        if (generazioneRef.current === generazione) {
          setFornitori(Array.isArray(data) ? data : data.items ?? []);
        }
      })
      .catch(() => {
        segnalaErroreCaricamento('fornitori');
        if (generazioneRef.current === generazione) setFornitori([]);
      });

    fetch('/api/v1/preventivi?tipo=vendita', authGet)
      .then((r) => {
        if (!r.ok) {
          segnalaErroreCaricamento('preventivi');
          return [];
        }
        return r.json();
      })
      .then((data) => {
        if (generazioneRef.current === generazione) setPreventivi(data.items ?? data ?? []);
      })
      .catch(() => {
        segnalaErroreCaricamento('preventivi');
        if (generazioneRef.current === generazione) setPreventivi([]);
      });

    fetch('/api/v1/archivio/cartelle', authGet)
      .then((r) => {
        if (!r.ok) {
          segnalaErroreCaricamento('cartelle');
          return [];
        }
        return r.json();
      })
      .then((data: Cartella[]) => {
        if (generazioneRef.current === generazione) setCartelle(data);
      })
      .catch(() => {
        segnalaErroreCaricamento('cartelle');
        if (generazioneRef.current === generazione) setCartelle([]);
      });

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [allegato]);

  const soggettiOptions = useMemo(() => {
    const lista = tipo === 'vendita' ? clienti : fornitori;
    return lista
      .filter((s) => nomeSoggetto(s).toLowerCase().includes(filtroSoggetto.toLowerCase()))
      .map((s) => ({ value: s.id, label: nomeSoggetto(s) }));
  }, [clienti, fornitori, tipo, filtroSoggetto]);

  const preventiviOptions = useMemo(
    () =>
      preventivi
        .filter((p) => p.numero.toLowerCase().includes(filtroPreventivo.toLowerCase()))
        .map((p) => ({ value: p.id, label: p.numero })),
    [preventivi, filtroPreventivo]
  );

  const cartelleOptions = useMemo(
    () =>
      cartelle
        .filter((c) => c.percorso_completo.toLowerCase().includes(filtroCartella.toLowerCase()))
        .map((c) => ({ value: c.id, label: c.percorso_completo })),
    [cartelle, filtroCartella]
  );

  const leggiErrore = async (res: Response) => {
    const body = await res.json().catch(() => null);
    return typeof body?.detail === 'string' ? body.detail : `Errore ${res.status}`;
  };

  const eseguiPost = async (forzaVuoto: boolean) => {
    if (!allegato) return;
    const generazione = generazioneRef.current;
    const body: Record<string, unknown> = { modo, forza_vuoto: forzaVuoto };
    if (modo === 'crea') Object.assign(body, { tipo, soggetto_id: soggettoId });
    if (modo === 'popola') Object.assign(body, { preventivo_id: preventivoId });
    if (modo === 'allega')
      Object.assign(body, {
        preventivo_id: preventivoId,
        cartella_id: cartellaId,
        genera_ordine: generaOrdine,
        riferimento: riferimento || null,
      });

    setInCorso(true);
    setErrore(null);
    setNessunaRiga(false);
    try {
      const res = await emailFetch(
        `/messaggi/${messaggioId}/allegati/${allegato.id}/carica-preventivo`,
        { method: 'POST', body: JSON.stringify(body) }
      );
      // Il dialog e' stato chiuso/riaperto su un altro allegato nel frattempo
      // (vale anche per la ripetizione con forza_vuoto): ignora la risposta
      // tardiva, non e' piu' la sessione corrente.
      if (generazioneRef.current !== generazione) return;
      if (res.status === 422 && !forzaVuoto) {
        const msg = await leggiErrore(res);
        if (generazioneRef.current !== generazione) return;
        if (msg.toLowerCase().includes('nessuna riga estratta')) {
          setNessunaRiga(true);
          setErrore(msg);
          return;
        }
        setErrore(msg);
        return;
      }
      if (!res.ok) {
        const msg = await leggiErrore(res);
        if (generazioneRef.current !== generazione) return;
        setErrore(msg);
        return;
      }
      const r: CaricaPreventivoResponse = await res.json();
      if (generazioneRef.current !== generazione) return;
      let esito = `${r.numero}: ${r.righe_estratte} righe estratte`;
      if (r.ordine_numero) esito += ` - ordine ${r.ordine_numero} generato`;
      setMessaggio(esito);
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        if (generazioneRef.current !== generazione) return;
        onClose();
        navigate(
          r.tipo === 'vendita' ? `/vendite/preventivi/${r.preventivo_id}` : `/preventivi/${r.preventivo_id}`
        );
      }, CHIUSURA_SUCCESSO_MS);
    } catch {
      if (generazioneRef.current === generazione) setErrore('Errore di rete');
    } finally {
      if (generazioneRef.current === generazione) setInCorso(false);
    }
  };

  const submitAbilitato =
    !inCorso &&
    !messaggio &&
    ((modo === 'crea' && !!soggettoId) ||
      (modo === 'popola' && !!preventivoId) ||
      (modo === 'allega' && !!preventivoId && !!cartellaId));

  return (
    <Dialog open={!!allegato} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Carica nel sistema</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            {(
              [
                { value: 'crea', label: 'Crea nuovo preventivo' },
                { value: 'popola', label: 'Popola preventivo vuoto' },
                { value: 'allega', label: 'Allega documento firmato' },
              ] as { value: Modo; label: string }[]
            ).map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="modo-carica-sistema"
                  value={opt.value}
                  checked={modo === opt.value}
                  onChange={() => setModo(opt.value)}
                  className="h-4 w-4 border-gray-300 text-blue-600"
                  disabled={inCorso}
                />
                <span className="text-sm text-foreground">{opt.label}</span>
              </label>
            ))}
          </div>

          {modo === 'crea' && (
            <>
              <div className="space-y-1.5">
                {(
                  [
                    { value: 'vendita', label: 'Vendita (cliente)' },
                    { value: 'acquisto', label: 'Acquisto (fornitore)' },
                  ] as { value: 'vendita' | 'acquisto'; label: string }[]
                ).map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="tipo-preventivo-crea"
                      value={opt.value}
                      checked={tipo === opt.value}
                      onChange={() => {
                        setTipo(opt.value);
                        setSoggettoId(null);
                        setFiltroSoggetto('');
                      }}
                      className="h-4 w-4 border-gray-300 text-blue-600"
                      disabled={inCorso}
                    />
                    <span className="text-sm text-foreground">{opt.label}</span>
                  </label>
                ))}
              </div>
              <div>
                <Label htmlFor="cs-soggetto">{tipo === 'vendita' ? 'Cliente' : 'Fornitore'}</Label>
                <Combobox
                  options={soggettiOptions}
                  value={filtroSoggetto}
                  onInputChange={(v) => {
                    setFiltroSoggetto(v);
                    setSoggettoId(null);
                  }}
                  onSelect={(opt) => {
                    setSoggettoId(opt.value);
                    setFiltroSoggetto(opt.label);
                  }}
                  placeholder={tipo === 'vendita' ? 'Cerca cliente...' : 'Cerca fornitore...'}
                  disabled={inCorso}
                />
              </div>
            </>
          )}

          {(modo === 'popola' || modo === 'allega') && (
            <div>
              <Label htmlFor="cs-preventivo">Preventivo</Label>
              <Combobox
                options={preventiviOptions}
                value={filtroPreventivo}
                onInputChange={(v) => {
                  setFiltroPreventivo(v);
                  setPreventivoId(null);
                }}
                onSelect={(opt) => {
                  setPreventivoId(opt.value);
                  setFiltroPreventivo(opt.label);
                }}
                placeholder="Cerca preventivo per numero..."
                disabled={inCorso}
              />
            </div>
          )}

          {modo === 'allega' && (
            <>
              <div>
                <Label htmlFor="cs-cartella">Cartella documentale</Label>
                <Combobox
                  options={cartelleOptions}
                  value={filtroCartella}
                  onInputChange={(v) => {
                    setFiltroCartella(v);
                    setCartellaId(null);
                  }}
                  onSelect={(opt) => {
                    setCartellaId(opt.value);
                    setFiltroCartella(opt.label);
                  }}
                  placeholder="Cerca cartella..."
                  disabled={inCorso}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="cs-genera-ordine"
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-blue-600"
                  checked={generaOrdine}
                  onChange={(e) => setGeneraOrdine(e.target.checked)}
                  disabled={inCorso}
                />
                <label htmlFor="cs-genera-ordine" className="text-sm text-foreground">
                  Genera ordine di vendita
                </label>
              </div>
              <div>
                <Label htmlFor="cs-riferimento">Riferimento (opzionale)</Label>
                <Input
                  id="cs-riferimento"
                  value={riferimento}
                  onChange={(e) => setRiferimento(e.target.value)}
                  disabled={inCorso}
                />
              </div>
            </>
          )}

          {errore && (
            <div className="space-y-2">
              <p className="text-sm text-destructive">{errore}</p>
              {nessunaRiga && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => eseguiPost(true)}
                  disabled={inCorso}
                >
                  Crea comunque vuoto
                </Button>
              )}
            </div>
          )}
          {messaggio && (
            <p className="text-sm text-green-700 dark:text-green-400">{messaggio}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={inCorso}>
            Annulla
          </Button>
          <Button onClick={() => eseguiPost(false)} disabled={!submitAbilitato}>
            {inCorso ? 'Carico...' : 'Carica'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
